import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle, XCircle, Eye, Loader2 } from 'lucide-react';
import { AdManager } from '@/components/ads/AdManager';

interface Opportunity {
  id: string;
  title: string;
  company: string;
  type: string;
  status: string;
  created_at: string;
}

export default function AdminDashboard() {
  const [user, setUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/auth');
        return;
      }

      setUser(user);

      // Check if user has admin role
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();

      if (!roles) {
        toast({
          title: 'Accès refusé',
          description: 'Vous n\'avez pas les permissions d\'administrateur',
          variant: 'destructive',
        });
        navigate('/');
        return;
      }

      setIsAdmin(true);
      loadOpportunities();
    } catch (error) {
      console.error('Auth check error:', error);
      navigate('/auth');
    } finally {
      setLoading(false);
    }
  };

  const loadOpportunities = async () => {
    try {
      const { data, error } = await supabase
        .from('opportunities')
        .select('id, title, company, type, status, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOpportunities(data || []);
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les opportunités',
        variant: 'destructive',
      });
    }
  };

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      const { error } = await supabase.rpc('change_opportunity_status', {
        opp_id: id,
        new_status: 'approved'
      });

      if (error) throw error;

      toast({
        title: 'Approuvée',
        description: 'L\'opportunité a été approuvée',
      });
      
      loadOpportunities();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    setActionLoading(id);
    try {
      const { error } = await supabase.rpc('change_opportunity_status', {
        opp_id: id,
        new_status: 'rejected'
      });

      if (error) throw error;

      toast({
        title: 'Rejetée',
        description: 'L\'opportunité a été rejetée',
      });
      
      loadOpportunities();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const pendingOpportunities = opportunities.filter(o => o.status === 'pending');
  const approvedOpportunities = opportunities.filter(o => o.status === 'approved');
  const rejectedOpportunities = opportunities.filter(o => o.status === 'rejected');

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Tableau de bord Admin</h1>
            <p className="text-muted-foreground mt-1">Bienvenue, {user?.email}</p>
          </div>
          <Button variant="outline" onClick={handleLogout}>
            Déconnexion
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">En attente</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-primary">{pendingOpportunities.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Approuvées</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-green-600">{approvedOpportunities.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Rejetées</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-red-600">{rejectedOpportunities.length}</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="opportunities" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="opportunities">Opportunités</TabsTrigger>
            <TabsTrigger value="ads">Publicités</TabsTrigger>
          </TabsList>
          
          <TabsContent value="opportunities">
            <Card>
              <CardHeader>
                <CardTitle>Opportunités en attente de validation</CardTitle>
                <CardDescription>
                  Approuvez ou rejetez les opportunités soumises
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {pendingOpportunities.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      Aucune opportunité en attente
                    </p>
                  ) : (
                    pendingOpportunities.map((opp) => (
                      <div key={opp.id} className="border rounded-lg p-4 flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{opp.title}</h3>
                          <p className="text-sm text-muted-foreground">{opp.company}</p>
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline">{opp.type}</Badge>
                            <Badge variant="secondary">{opp.status}</Badge>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/opportunities/${opp.id}`)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleApprove(opp.id)}
                            disabled={actionLoading === opp.id}
                          >
                            {actionLoading === opp.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <CheckCircle className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleReject(opp.id)}
                            disabled={actionLoading === opp.id}
                          >
                            {actionLoading === opp.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <XCircle className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="ads">
            <AdManager />
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}
